import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';
import { useNotificationStore } from '@stores/notificationStore';
import { parseExpiryDateInput } from '@lib/licenseUpdate';

const NEXT_STATUS: Record<string, Array<{ value: string; label: string }>> = {
  draft: [
    { value: 'sent', label: 'Mark Sent' },
    { value: 'cancelled', label: 'Cancel' },
  ],
  sent: [
    { value: 'paid', label: 'Mark Paid' },
    { value: 'overdue', label: 'Mark Overdue' },
    { value: 'cancelled', label: 'Cancel' },
  ],
  overdue: [
    { value: 'paid', label: 'Mark Paid' },
    { value: 'cancelled', label: 'Cancel' },
  ],
};

const Invoices: React.FC = () => {
  const { addNotification } = useNotificationStore();
  const [showNewForm, setShowNewForm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_id: '',
    description: '',
    quantity: 1,
    unit_price: '',
    invoice_date: '',
    due_date: '',
    notes: '',
  });

  const { data: invoicesData, refetch } = useQuery({
    queryKey: ['invoices-list'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/invoices?limit=100');
      return response;
    },
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/customers?limit=100');
      return response;
    },
  });
  const customers = customersData?.items || [];

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    const unitCents = Math.round(parseFloat(newInvoice.unit_price) * 100);
    const quantity = Math.floor(newInvoice.quantity);
    const invoiceDate = parseExpiryDateInput(newInvoice.invoice_date);
    const dueDate = parseExpiryDateInput(newInvoice.due_date);
    if (!newInvoice.customer_id) {
      addNotification({ message: 'Error: select a customer', type: 'error' });
      return;
    }
    if (!Number.isFinite(unitCents) || unitCents <= 0 || quantity <= 0) {
      addNotification({ message: 'Error: enter a valid quantity and unit price', type: 'error' });
      return;
    }
    if (invoiceDate === null || dueDate === null || dueDate < invoiceDate) {
      addNotification({ message: 'Error: due date must be on or after the invoice date', type: 'error' });
      return;
    }
    setIsSaving(true);
    try {
      const totalCents = quantity * unitCents;
      await apiClient.post('/api/invoices', {
        customer_id: newInvoice.customer_id,
        amount_cents: totalCents,
        currency: 'USD',
        invoice_date: invoiceDate,
        due_date: dueDate,
        line_items: [
          {
            description: newInvoice.description || 'Services',
            quantity,
            unit_price_cents: unitCents,
            total_cents: totalCents,
          },
        ],
        notes: newInvoice.notes || undefined,
      });
      setNewInvoice({
        customer_id: '',
        description: '',
        quantity: 1,
        unit_price: '',
        invoice_date: '',
        due_date: '',
        notes: '',
      });
      setShowNewForm(false);
      await refetch();
      addNotification({ message: 'Invoice created successfully', type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create invoice';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to create invoice:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStatus = async (invoiceId: string, status: string) => {
    try {
      await apiClient.put(`/api/invoices/${invoiceId}`, { status });
      await refetch();
      addNotification({ message: `Invoice marked as ${status}`, type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update invoice';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to update invoice:', error);
    }
  };

  const invoices = invoicesData?.items || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showNewForm ? 'Cancel' : 'New Invoice'}
        </button>
      </div>

      {showNewForm && (
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold mb-4">Create Invoice</h2>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
              <select
                value={newInvoice.customer_id}
                onChange={(e) => setNewInvoice({ ...newInvoice, customer_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a customer…</option>
                {customers.map((c: any) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newInvoice.description}
                onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                placeholder="Services"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={newInvoice.quantity}
                  onChange={(e) => setNewInvoice({ ...newInvoice, quantity: parseInt(e.target.value, 10) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price (USD)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={newInvoice.unit_price}
                  onChange={(e) => setNewInvoice({ ...newInvoice, unit_price: e.target.value })}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                <input
                  type="date"
                  value={newInvoice.invoice_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={newInvoice.due_date}
                  onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                value={newInvoice.notes}
                onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Invoice ID</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Customer</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No invoices yet. Create customers and issue invoices.
                </td>
              </tr>
            ) : (
              invoices.map((invoice: any) => (
                <tr key={invoice.invoice_id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-6 py-4 text-xs font-mono text-gray-900 break-all">{invoice.invoice_id}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{invoice.customer_id}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    ${((invoice.amount_cents ?? 0) / 100).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : invoice.status === 'sent'
                            ? 'bg-blue-100 text-blue-800'
                            : invoice.status === 'overdue'
                              ? 'bg-yellow-100 text-yellow-800'
                              : invoice.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {invoice.invoice_date ? new Date(invoice.invoice_date * 1000).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                    {(NEXT_STATUS[invoice.status] || []).map((action) => (
                      <button
                        key={action.value}
                        onClick={() => handleUpdateStatus(invoice.invoice_id, action.value)}
                        className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {action.label}
                      </button>
                    ))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Invoices;
